import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {AppSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import messageTemplateUpsellCardImage from '../../../../assets/images/analyticsMessageTemplateUpsellCard.svg';
import slaUpsellCardImage from '../../../../assets/images/analyticsSlaUpsellCard.svg';
import tagsUpsellCardImage from '../../../../assets/images/analyticsTagsUpsellCard.svg';
import analyticsUpsellCardImage from '../../../../assets/images/analyticsUpsellCard.svg';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  getCompanyHasAdvancedAnalytics,
  getCompanyHasAnalytics,
  getCompanyHasSlaRulesFeature,
} from '../../../../store/data/company/companySelectors';
import {AnalyticsReportsEnum} from '../../../helpers/analyticsReportHelpers';
import {AnalyticsUpsellCard, AnalyticsUpsellCardProps} from './analyticsUpsellCard';

/*
 * Constants.
 */

interface ReportUpsellCard extends AnalyticsUpsellCardProps {
  featureSelector: AppSelector<boolean>;
}

type UpsellCardKeys =
  | 'analytics'
  | AnalyticsReportsEnum.SLA
  | AnalyticsReportsEnum.TAGS
  | AnalyticsReportsEnum.MESSAGE_TEMPLATES;

const UPSELL_CARDS: Record<UpsellCardKeys, ReportUpsellCard> = {
  analytics: {
    featureSelector: getCompanyHasAnalytics,
    title: <Trans id="analytics-upsell-card-title">Elevate your customer experience</Trans>,
    body: (
      <Trans id="analytics-upsell-card-body">
        Keep an eye on your team response times and volume of mail, and discover ways to improve over time.
      </Trans>
    ),
    illustration: analyticsUpsellCardImage,
    helpPage: 'https://front.com/classroom/analytics-101',
  },
  [AnalyticsReportsEnum.SLA]: {
    featureSelector: getCompanyHasSlaRulesFeature,
    title: (
      <Trans id="analytics-sla-report-upsell-card-title">Elevate your customer experience with SLAs</Trans>
    ),
    body: (
      <Trans id="analytics-sla-report-upsell-card-body">
        Make sure you never miss a service level agreement or deadline, and help your team improve
      </Trans>
    ),
    illustration: slaUpsellCardImage,
    helpPage: 'https://front.com/classroom/sla-report',
  },
  [AnalyticsReportsEnum.MESSAGE_TEMPLATES]: {
    featureSelector: getCompanyHasAdvancedAnalytics,
    title: (
      <Trans id="analytics-message-templates-report-upsell-card-title">
        Measure impact of message templates
      </Trans>
    ),
    body: (
      <Trans id="analytics-message-template-report-upsell-card-body">
        Monitor how message templates are used in your organization to continuously improve quality and
        efficiency of your communications.
      </Trans>
    ),
    illustration: messageTemplateUpsellCardImage,
    helpPage: 'https://help.front.com/t/y4hg2md/message-templates-report',
  },
  [AnalyticsReportsEnum.TAGS]: {
    featureSelector: getCompanyHasAdvancedAnalytics,
    title: <Trans id="analytics-tags-report-upsell-card-title">Monitor conversation topic trends</Trans>,
    body: (
      <Trans id="analytics-tags-report-upsell-card-body">
        Get insight into the topics your team is working on the most &mdash; so you can improve over time.
      </Trans>
    ),
    illustration: tagsUpsellCardImage,
    helpPage: 'https://front.com/classroom/tags-report',
  },
};

/*
 * Props.
 */

export interface UpsellCardOverlayProps {
  reportType: AnalyticsReportsEnum;
}

/*
 * Styles.
 */

const StyledOverlay = styled.div`
  ${flexCenter}
  justify-content: center;

  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${(p) => p.theme.alphas.white20};
  backdrop-filter: blur(5px);
`;

/*
 * Component.
 */

export const UpsellCardOverlay: FC<UpsellCardOverlayProps> = ({reportType}) => {
  let reportSelector: AppSelector<boolean> = () => true;
  if (reportHasUpsell(reportType)) {
    reportSelector = UPSELL_CARDS[reportType].featureSelector;
  }

  const hasAnalytics = useAppSelector(getCompanyHasAnalytics);
  const hasReportFeature = useAppSelector(reportSelector);

  if (hasAnalytics && hasReportFeature) {
    return null;
  }

  const {title, body, illustration, helpPage} =
    UPSELL_CARDS[
      // reportHasUpsell should always return true here, it acts as a type-guard.
      hasAnalytics && reportHasUpsell(reportType) ? reportType : 'analytics'
    ];

  return (
    <StyledOverlay>
      <AnalyticsUpsellCard title={title} body={body} illustration={illustration} helpPage={helpPage} />
    </StyledOverlay>
  );
};

/*
 * Helpers.
 */

function reportHasUpsell(reportType: AnalyticsReportsEnum | string): reportType is UpsellCardKeys {
  return reportType in UPSELL_CARDS;
}
